import { useState } from 'react';
import { Link } from 'react-router-dom';

import type { UserActivityEntry } from '../services/user-activity.service';
import { formatActivityDate, typeMeta } from '../lib/activityMeta';
import { ActivityIcon, LevelBadge } from './ActivityBadges';

/**
 * Una riga della timeline. Gli errori sono evidenziati in rosso, gli avvisi in
 * ambra; il dettaglio tecnico (meta JSON) si apre a richiesta.
 */
export function ActivityEntryRow({ entry, compact = false }: { entry: UserActivityEntry; compact?: boolean }) {
  const isError = entry.level === 'error';
  const hasMeta = Boolean(entry.meta && Object.keys(entry.meta).length > 0);
  const [open, setOpen] = useState(false);
  const m = typeMeta(entry.type);
  const callSessionId = typeof entry.meta?.callSessionId === 'string' ? entry.meta.callSessionId : null;

  const rowTone = isError
    ? 'bg-red-50/70 border-l-4 border-red-500'
    : entry.level === 'warning'
      ? 'border-l-4 border-amber-400'
      : 'border-l-4 border-transparent';

  return (
    <li className={`flex gap-3 px-4 py-3 ${rowTone}`}>
      <ActivityIcon type={entry.type} level={entry.level} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-xs font-semibold uppercase tracking-wide text-neutral-500">{m.label}</span>
          <LevelBadge level={entry.level} />
          <span className="text-xs text-neutral-400">{formatActivityDate(entry.createdAt)}</span>
        </div>
        <p className={`mt-0.5 break-words text-sm ${isError ? 'font-medium text-red-800' : 'text-neutral-800'}`}>
          {entry.message}
        </p>
        {!compact && (hasMeta || callSessionId) && (
          <div className="mt-1 flex flex-wrap items-center gap-3">
            {hasMeta && (
              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="text-xs font-medium text-brand-teal hover:underline"
                aria-expanded={open}
              >
                {open ? 'Nascondi dettagli tecnici' : 'Dettagli tecnici'}
              </button>
            )}
            {callSessionId && (
              <Link to={`/dashboard/admin/calls/${callSessionId}`} className="text-xs font-medium text-brand-teal hover:underline">
                Apri chiamata
              </Link>
            )}
          </div>
        )}
        {!compact && open && hasMeta && (
          <pre
            className={`mt-2 max-h-72 overflow-auto whitespace-pre-wrap break-all rounded-lg p-3 text-xs ${
              isError ? 'bg-red-100/70 text-red-900' : 'bg-neutral-50 text-neutral-700'
            }`}
          >
            {JSON.stringify(entry.meta, null, 2)}
          </pre>
        )}
      </div>
    </li>
  );
}
